import * as THREE from 'three';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { config } from './config.js';
import { createMaterials } from './scene/materials.js';
import { buildProceduralAirframe } from './scene/airframe.js';
import { createExplodedController } from './scene/exploded.js';
import { createMorphController } from './scene/morphs.js';
import { createCameraController } from './scene/camera.js';
import { initRenderLoop } from './scene/loop.js';

import { initTelemetry } from './ui/telemetry.js';
import { initCustomCursor } from './ui/cursor.js';
import { runBootSequence } from './ui/boot.js';
import { initRail } from './ui/rail.js';
import { initProjectOverlay } from './ui/overlay.js';
import { initContactForm } from './ui/form.js';

gsap.registerPlugin(ScrollTrigger);

/**
 * APPLICATION ENTRY POINT // KEVIN ARTHUR DE LIMA PORTFOLIO
 */

function initContent() {
  // 1. Render Systems / Skills
  const skillsListEl = document.getElementById('skills-list-container');
  if (skillsListEl) {
    skillsListEl.innerHTML = config.skills.map(skill => `
      <div class="skill-row" data-subsystem="${skill.partHighlight}" tabindex="0" role="button" aria-label="Inspect ${skill.title} subsystem">
        <div class="skill-row-head">
          <span class="skill-title">${skill.title}</span>
          <span class="skill-code">${skill.code}</span>
        </div>
        <div class="skill-tags">
          ${skill.items.map(item => `<span class="skill-tag">${item}</span>`).join('')}
        </div>
      </div>
    `).join('');
  }

  // 2. Render Hardware Projects (DWG-003)
  const projectsFeedEl = document.getElementById('projects-feed-container');
  if (projectsFeedEl) {
    projectsFeedEl.innerHTML = config.projects.map(proj => `
      <article class="project-card" data-morph="${proj.configuration}">
        <div class="project-card-grid">
          <div class="project-left">
            <div class="project-dwg-tag">${proj.dwg} // ${proj.projectNo} // ${proj.rev}</div>
            <h3 class="project-title">${proj.title}</h3>
            <div class="mono-label" style="margin-bottom: 12px; color: var(--sheet-mute);">
              ROLE: ${proj.role} // ${proj.status}
            </div>
            <p class="project-hero-line">${proj.heroLine}</p>
            <ul class="project-bullets">
              ${proj.bullets.map(b => `<li>${b}</li>`).join('')}
            </ul>
            <div class="project-stack-wrap">
              ${proj.stack.map(s => `<span class="stack-pill">${s}</span>`).join('')}
            </div>
            <button class="project-open-btn" data-project-slug="${proj.slug}">
              [ INSPECT FULL DWG / SPECIFICATION ]
            </button>
          </div>

          <div class="project-right">
            <div class="project-thumb-frame" data-project-slug="${proj.slug}">
              <img src="${proj.image}" alt="${proj.title}" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
              <div class="project-thumb-placeholder" style="display: none;">
                CAD SCHEMATIC FILE<br>
                [ VIEW SPECIFICATION ]
              </div>
            </div>
            <table class="title-block-table">
              <tr><th>MATERIAL</th><td>${proj.material}</td></tr>
              <tr><th>MASS</th><td>${proj.mass}</td></tr>
              <tr><th>POWER</th><td>${proj.power}</td></tr>
              <tr><th>DATE</th><td>${proj.date}</td></tr>
            </table>
          </div>
        </div>
      </article>
    `).join('');
  }

  // 3. Render Software Systems Index Table (Prometheus Dynamics)
  const softwareTableBody = document.getElementById('software-table-body');
  if (softwareTableBody) {
    softwareTableBody.innerHTML = config.softwareProducts.map(prod => `
      <tr>
        <td class="software-id">${prod.id}</td>
        <td class="software-name">
          <div>${prod.name}</div>
          <div class="mono-label" style="font-size: 0.625rem; margin-top: 4px; color: var(--sheet-mute);">${prod.status}</div>
        </td>
        <td class="software-line">${prod.oneLine}</td>
      </tr>
    `).join('');
  }

  // 4. Render Flight Path Experience (DWG-004)
  const experienceContainer = document.getElementById('experience-timeline-container');
  if (experienceContainer) {
    experienceContainer.innerHTML = config.experience.map(exp => `
      <div class="waypoint-card">
        <div class="waypoint-node">
          <div class="waypoint-node-inner"></div>
        </div>
        <div class="waypoint-meta">
          <span class="waypoint-id">${exp.waypoint}</span>
          <span class="waypoint-alt">ALTITUDE: ${exp.alt}</span>
          <span class="waypoint-period">${exp.period}</span>
          <span class="waypoint-period">${exp.location}</span>
        </div>
        <h3 class="waypoint-role">${exp.role}</h3>
        <div class="waypoint-entity">${exp.entity}</div>
        <ul class="waypoint-bullets">
          ${exp.bullets.map(b => `<li>${b}</li>`).join('')}
        </ul>
      </div>
    `).join('');
  }

  // 5. Render Education (DWG-005)
  const educationContainer = document.getElementById('education-list-container');
  if (educationContainer) {
    educationContainer.innerHTML = config.education.map(edu => `
      <div class="edu-card">
        <h3 class="edu-degree">${edu.degree}</h3>
        <div class="edu-institution">${edu.institution}</div>
        <div class="edu-meta">
          <span>${edu.period}</span>
          <span class="edu-metric">${edu.metric}</span>
        </div>
        ${edu.notes ? `<p style="font-size: 0.8125rem; color: var(--sheet-mute); margin-top: 10px;">${edu.notes}</p>` : ''}
      </div>
    `).join('');
  }
}

function initSmoothScroll() {
  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (isReducedMotion) return null;

  const lenis = new Lenis({
    duration: 1.1,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    smoothTouch: false,
  });

  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });
  gsap.ticker.lagSmoothing(0);

  return lenis;
}

function initWebGLScene() {
  const container = document.getElementById('webgl-canvas-container');
  const fallback = document.getElementById('webgl-fallback');
  if (!container) return null;

  // Verify WebGL availability
  let isWebGLAvailable = false;
  try {
    const canvas = document.createElement('canvas');
    isWebGLAvailable = !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) {
    isWebGLAvailable = false;
  }

  if (!isWebGLAvailable) {
    if (fallback) fallback.style.display = 'block';
    return null;
  }

  // Setup Three.js Renderer
  const isMobile = window.innerWidth < 768;
  const renderer = new THREE.WebGLRenderer({
    powerPreference: 'high-performance',
    antialias: !isMobile,
    alpha: true
  });

  const maxDPR = isMobile ? config.model3d.mobilePixelRatio : config.model3d.maxPixelRatio;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxDPR));
  renderer.setSize(window.innerWidth, window.innerHeight);
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();

  // Low FOV orthographic feel
  const cameraController = createCameraController(config.model3d.cameraFov);

  // Subtle Key Lighting
  const dirLight = new THREE.DirectionalLight(0xFFFFFF, 0.9);
  dirLight.position.set(5, 12, 7);
  scene.add(dirLight);

  const ambLight = new THREE.AmbientLight(0x222222, 1.0);
  scene.add(ambLight);

  // Create Materials & Procedural Drone Airframe
  const materials = createMaterials();
  const airframe = buildProceduralAirframe(materials);
  scene.add(airframe.root);

  const explodedController = createExplodedController(airframe.groups, materials);
  const morphController = createMorphController(airframe.groups);

  // Resize handler
  window.addEventListener('resize', () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, width < 768 ? 1.5 : 2));
  });

  return {
    scene,
    renderer,
    cameraController,
    airframe,
    explodedController,
    morphController,
    materials
  };
}

// APPLICATION BOOTSTRAP
window.addEventListener('DOMContentLoaded', () => {
  // 1. Populate dynamic copy and records from config.js
  initContent();

  // 2. Initialize smooth scrolling with Lenis + ScrollTrigger
  const lenis = initSmoothScroll();

  // 3. Initialize custom crosshair cursor
  initCustomCursor();

  // 4. Initialize left index rail
  initRail(config.sections);

  // 5. Initialize full-screen drawing sheet overlay
  const overlayController = initProjectOverlay(config.projects, lenis);
  if (overlayController) {
    document.querySelectorAll('[data-project-slug]').forEach(btn => {
      btn.addEventListener('click', e => {
        const slug = btn.getAttribute('data-project-slug');
        if (slug) overlayController.open(slug);
      });
    });

    const recBtn = document.getElementById('open-rec-letter-btn');
    if (recBtn && config.recommendation) {
      recBtn.addEventListener('click', () => {
        overlayController.openDocument(config.recommendation);
      });
    }
  }

  // 6. Initialize contact form handler
  initContactForm();

  // 7. Initialize WebGL 3D Scene
  const webgl = initWebGLScene();

  let loopController = null;
  if (webgl) {
    const updateTelemetry = initTelemetry(
      config,
      () => loopController ? loopController.toggleWireframeMode() : "SOLID + EDGES"
    );

    loopController = initRenderLoop({
      scene: webgl.scene,
      cameraController: webgl.cameraController,
      renderer: webgl.renderer,
      airframe: webgl.airframe,
      explodedController: webgl.explodedController,
      morphController: webgl.morphController,
      materials: webgl.materials,
      onTelemetryUpdate: updateTelemetry
    });

    // Subsystem highlighting on hover in Systems section
    const subIndicator = document.getElementById('subsystem-active-indicator');
    document.querySelectorAll('.skill-row').forEach(row => {
      const partKey = row.getAttribute('data-subsystem');
      const title = row.querySelector('.skill-title')?.textContent || partKey;

      row.addEventListener('mouseenter', () => {
        webgl.explodedController.highlightSubsystem(partKey);
        if (subIndicator) subIndicator.textContent = `ISOLATING: ${title.toUpperCase()}`;
        row.classList.add('active');
      });

      row.addEventListener('mouseleave', () => {
        webgl.explodedController.clearHighlight();
        if (subIndicator) subIndicator.textContent = "ALL SUBSYSTEMS NOMINAL";
        row.classList.remove('active');
      });

      row.addEventListener('focus', () => {
        webgl.explodedController.highlightSubsystem(partKey);
        if (subIndicator) subIndicator.textContent = `ISOLATING: ${title.toUpperCase()}`;
      });

      row.addEventListener('blur', () => {
        webgl.explodedController.clearHighlight();
        if (subIndicator) subIndicator.textContent = "ALL SUBSYSTEMS NOMINAL";
      });
    });
  } else {
    initTelemetry(config, null);
  }

  // 8. Flight controller boot sequence
  runBootSequence(() => {
    document.body.classList.add('booted');
    ScrollTrigger.refresh();
  });
});
